import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Save, Loader2, PenTool, CheckCircle, XCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DocuSignSettings = () => {
  const { token } = useAuth();
  const [environment, setEnvironment] = useState('sandbox');
  const [clientId, setClientId] = useState('');
  const [userId, setUserId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [hasKey, setHasKey] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [authServer, setAuthServer] = useState('account-d.docusign.com');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(API_URL + '/api/docusign/settings?authorization=' + token)
      .then((r) => {
        setEnvironment(r.data.environment || 'sandbox');
        setClientId(r.data.client_id || '');
        setUserId(r.data.user_id || '');
        setAccountId(r.data.account_id || '');
        setHasKey(r.data.has_private_key);
        setConfigured(r.data.configured);
        setAuthServer(r.data.auth_server);
      })
      .catch(() => {});
  }, [token]);

  const redirectUri = window.location.origin + '/api/docusign/callback';
  const consentUrl = 'https://' + authServer + '/oauth/auth?response_type=code&scope=signature%20impersonation&client_id=' + clientId + '&redirect_uri=' + encodeURIComponent(redirectUri);

  const save = () => {
    setSaving(true);
    axios.post(API_URL + '/api/docusign/settings?authorization=' + token, {
      environment, client_id: clientId, user_id: userId, account_id: accountId,
      private_key: privateKey || null,
    })
      .then((r) => {
        toast.success('DocuSign settings saved');
        setConfigured(r.data.configured);
        if (privateKey) { setHasKey(true); setPrivateKey(''); }
        setAuthServer(environment === 'production' ? 'account.docusign.com' : 'account-d.docusign.com');
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const testConnection = () => {
    setTesting(true);
    setTestResult(null);
    axios.post(API_URL + '/api/docusign/test?authorization=' + token)
      .then((r) => setTestResult(r.data))
      .catch(() => setTestResult({ success: false, message: 'Test request failed' }))
      .finally(() => setTesting(false));
  };

  return (
    <Card data-testid="docusign-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><PenTool className="w-5 h-5 text-[#8CC63F]" />DocuSign eSignature</CardTitle>
            <CardDescription>Send C&I contracts for electronic signature. Get credentials from DocuSign → Settings → Apps and Keys.</CardDescription>
          </div>
          {configured ? <Badge className="bg-green-100 text-green-700">Configured</Badge> : <Badge variant="outline" className="text-amber-600 border-amber-300">Not configured</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Environment</Label>
            <Select value={environment} onValueChange={setEnvironment}>
              <SelectTrigger data-testid="ds-environment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox / Developer (demo.docusign.net)</SelectItem>
                <SelectItem value="production">Production (requires Go-Live approval)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Integration Key (Client ID)</Label>
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="ds-client-id" />
          </div>
          <div>
            <Label>User ID (impersonated user GUID)</Label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="ds-user-id" />
          </div>
          <div>
            <Label>API Account ID</Label>
            <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="ds-account-id" />
          </div>
        </div>
        <div>
          <Label>RSA Private Key {hasKey && <span className="text-green-600 text-xs">(already saved — paste again only to replace)</span>}</Label>
          <Textarea rows={5} value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'} className="font-mono text-xs" data-testid="ds-private-key" />
        </div>

        <div className="flex gap-3">
          <Button onClick={save} disabled={saving} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="ds-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Settings
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing || !configured} data-testid="ds-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Test Connection
          </Button>
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="ds-test-result">
            {testResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <div>
              <p>{testResult.message}</p>
              {testResult.error === 'consent_required' && (
                <a href={consentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 font-semibold underline" data-testid="ds-consent-link">
                  <ExternalLink className="w-3.5 h-3.5" />Open consent page (one-time)
                </a>
              )}
            </div>
          </div>
        )}

        <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-600 space-y-2">
          <p className="font-semibold text-slate-700">Setup checklist:</p>
          <p>1. In DocuSign → Apps and Keys, add this <strong>Redirect URI</strong> to your app: <code className="bg-white px-1.5 py-0.5 rounded border">{redirectUri}</code></p>
          <p>2. Save your credentials above, then complete the one-time consent: {clientId ? <a href={consentUrl} target="_blank" rel="noopener noreferrer" className="text-[#015492] underline">open consent link</a> : <span>enter Integration Key first</span>}</p>
          <p>3. Click "Test Connection" — once it succeeds, staff can send contracts for signature from C&I History.</p>
        </div>
      </CardContent>
    </Card>
  );
};
